import { uploadSellerImage } from "../../lib/imageUploads";
import ResponsiveImage from "../../components/common/ResponsiveImage";
import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  AlertCircle,
  Camera,
  Loader2,
  ImagePlus,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../lib/utils";
import ShopImageCropper from "../../components/portal/ShopImageCropper";
import { pickAndValidateImage } from "../../lib/pickImage";
import { getShopTextErrors } from "../../lib/shopTextSafety";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TAGLINE_MAX = 50;
const DESCRIPTION_MAX = 250;
const BANNER_ASPECT = 3.2;
const LOGO_ASPECT = 1;

// Image constraints (frontend — best-practice sizes for quality renders)
const LOGO_MIN = { width: 400, height: 400 };
const BANNER_MIN = { width: 1200, height: 400 };

/* ------------ Counter ------------ */
const CharCounter = ({ value, max, testid }) => {
  usePartnerTranslation();
  const len = (value || "").length;
  const almost = len >= max * 0.9;
  const over = len > max;
  return (
    <span
      data-testid={testid}
      className={cn(
        "text-xs tabular-nums",
        over
          ? "text-[#9B0000]"
          : almost
            ? "text-[#B76A00]"
            : "text-[#9BA1AB]",
      )}
    >
      {len}/{max}
    </span>
  );
};

/* ------------ File picker helper ------------ */
const pickImage = () =>
  new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return resolve(null);
      if (file.size > 6 * 1024 * 1024) {
        toast.error(tr("Image too large"), {
          description: tr("Please pick a file under 6 MB."),
        });
        return resolve(null);
      }
      resolve(file);
    };
    input.click();
  });

/* ------------ Banner block ------------ */
const BannerBlock = ({
  url,
  onEdit,
  onRemove
}) => {
  usePartnerTranslation();
  return <div data-testid="shop-banner-block" className="relative overflow-hidden rounded-xl border border-[#EAECEF] bg-[#F3EEEE]" style={{
    aspectRatio: `${BANNER_ASPECT}`
  }}>
    {url ? <ResponsiveImage imageWidth={960} src={url} alt={tr("Shop banner")} className="h-full w-full object-cover" /> : <div className="flex h-full w-full flex-col items-center justify-center text-[#9BA1AB]">
        <ImagePlus className="h-10 w-10" strokeWidth={1.3} />
        <p className="mt-2 text-sm">{tr("No banner yet")}</p>
      </div>}
    <div className="absolute bottom-3 end-3 flex items-center gap-2">
      {url && <button type="button" onClick={onRemove} data-testid="shop-banner-remove" className="inline-flex h-9 items-center gap-2 rounded-lg bg-white/95 px-3 text-xs font-semibold text-[#9B0000] shadow-sm backdrop-blur transition-colors hover:bg-white">
          <Trash2 className="h-3.5 w-3.5" />{tr("Remove")}</button>}
      <button type="button" onClick={onEdit} data-testid="shop-banner-edit" className="inline-flex h-9 items-center gap-2 rounded-lg bg-white/95 px-3 text-xs font-semibold text-[#0F1115] shadow-sm backdrop-blur transition-colors hover:bg-white">
        <Camera className="h-3.5 w-3.5" />
        {url ? tr("Change banner") : tr("Upload banner")}
      </button>
    </div>
  </div>;
};

/* ------------ Logo block ------------ */
const LogoBlock = ({
  url,
  onEdit,
  onRemove
}) => {
  usePartnerTranslation();
  return <div className="flex items-center gap-5">
    <div data-testid="shop-logo-block" className="relative flex h-28 w-28 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-[#EAECEF] bg-[#FFD8D8]">
      {url ? <ResponsiveImage imageWidth={960} src={url} alt={tr("Shop logo")} className="h-full w-full object-cover" /> : <Camera className="h-8 w-8 text-[#FF5757]" strokeWidth={1.4} />}
    </div>
    <div className="flex flex-wrap gap-2">
      <button type="button" onClick={onEdit} data-testid="shop-logo-edit" className="inline-flex h-9 items-center gap-2 rounded-md border border-[#0F1115] px-3 text-xs font-semibold text-[#0F1115] transition-colors hover:bg-[#0F1115] hover:text-white">
        <Camera className="h-3.5 w-3.5" />
        {url ? tr("Change logo") : tr("Upload logo")}
      </button>
      {url && <button type="button" onClick={onRemove} data-testid="shop-logo-remove" className="inline-flex h-9 items-center gap-2 rounded-md border border-[#E6E8EC] px-3 text-xs font-semibold text-[#9B0000] transition-colors hover:border-[#9B0000]">
          <Trash2 className="h-3.5 w-3.5" />{tr("Remove")}</button>}
    </div>
  </div>;
};

/* ------------ Page ------------ */
export default function EditShopPage() {
  usePartnerTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState({
    name: "",
    handle: "",
    tagline: "",
    bio: "",
    profile_image: "",
    cover_image: "",
  });

  const [cropper, setCropper] = useState({
    open: false,
    target: null, // 'logo' | 'banner'
    src: null,
  });

  // Load current shop
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/shops/me`);
        if (cancelled) return;
        const s = res.data;
        setOriginal(s);
        setForm({
          name: s.name || "",
          handle: s.handle || "",
          tagline: s.tagline || "",
          bio: s.bio || "",
          profile_image: s.profile_image || "",
          cover_image: s.cover_image || "",
        });
      } catch (err) {
        toast.error(tr("Couldn't load your shop"), {
          description: err?.response?.data?.detail || "Please try again.",
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isDirty = useMemo(() => {
    if (!original) return false;
    return (
      (form.tagline || "") !== (original.tagline || "") ||
      (form.bio || "") !== (original.bio || "") ||
      (form.profile_image || "") !== (original.profile_image || "") ||
      (form.cover_image || "") !== (original.cover_image || "")
    );
  }, [form, original]);

  const fieldErrors = useMemo(
    () => getShopTextErrors(form.tagline, form.bio),
    [form.tagline, form.bio],
  );

  const canSave =
    isDirty &&
    !saving &&
    (form.tagline || "").length <= TAGLINE_MAX &&
    (form.bio || "").length <= DESCRIPTION_MAX &&
    !fieldErrors.tagline &&
    !fieldErrors.bio;

  const openLogoCropper = useCallback(async () => {
    const res = await pickAndValidateImage({
      minWidth: LOGO_MIN.width,
      minHeight: LOGO_MIN.height,
      label: tr("Logo"),
    });
    if (!res) return;
    setCropper({ open: true, target: "logo", src: res.dataUrl });
  }, []);

  const openBannerCropper = useCallback(async () => {
    const res = await pickAndValidateImage({
      minWidth: BANNER_MIN.width,
      minHeight: BANNER_MIN.height,
      label: tr("Banner"),
    });
    if (!res) return;
    setCropper({ open: true, target: "banner", src: res.dataUrl });
  }, []);

  const handleCropApply = (dataUrl) => {
    if (cropper.target === "logo") {
      setForm((f) => ({ ...f, profile_image: dataUrl }));
    } else if (cropper.target === "banner") {
      setForm((f) => ({ ...f, cover_image: dataUrl }));
    }
    setCropper({ open: false, target: null, src: null });
  };

  const resetField = (field) => setForm((f) => ({ ...f, [field]: "" }));

  const save = async () => {
    const currentFieldErrors = getShopTextErrors(form.tagline, form.bio);
    if (currentFieldErrors.tagline || currentFieldErrors.bio) {
      toast.error(tr("Remove contact details"), {
        description:
          currentFieldErrors.tagline ||
          currentFieldErrors.bio ||
          "Company tagline and description can't include emails, phone numbers, or websites.",
      });
      return;
    }
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        tagline: form.tagline.trim(),
        bio: form.bio.trim(),
        profile_image: await uploadSellerImage(form.profile_image, "profile"),
        cover_image: await uploadSellerImage(form.cover_image, "profile"),
      };
      const res = await axios.put(`${API}/shops/me`, payload);
      const updated = res.data?.shop || res.data || {};
      const handle = updated.handle || form.handle || original?.handle || "";
      setOriginal(updated);
      setForm({
        name: updated.name || "",
        handle,
        tagline: updated.tagline || "",
        bio: updated.bio || "",
        profile_image: updated.profile_image || "",
        cover_image: updated.cover_image || "",
      });
      window.dispatchEvent(
        new CustomEvent("humrahe:shop-updated", { detail: { ...updated, handle } }),
      );
      toast.success(tr("Shop details saved"), {
        description: tr("Your shop is live at @{{value0}}.", { value0: handle }),
      });
    } catch (err) {
      toast.error(tr("Couldn't save changes"), {
        description: err?.response?.data?.detail || "Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    if (!original) return;
    setForm({
      name: original.name || "",
      handle: original.handle || "",
      tagline: original.tagline || "",
      bio: original.bio || "",
      profile_image: original.profile_image || "",
      cover_image: original.cover_image || "",
    });
    toast.info(tr("Changes discarded"));
  };

  if (loading) {
    return (
      <main className="flex flex-1 items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF5757]" />
      </main>
    );
  }

  const shopHandle = (form.handle || original?.handle || "").replace(/^@+/, "");
  const shopUrl = shopHandle ? `https://${shopHandle}.voocly.com` : "";

  return (
    <main
      data-testid="edit-shop-page"
      className="mx-auto w-full max-w-[960px] flex-1 px-4 pb-32 pt-10 sm:px-6 md:px-10"
    >
      <header>
        <h1 className="text-3xl font-extrabold tracking-tight text-[#0F1115] md:text-4xl">{tr("Edit shop details")}</h1>
        <p className="mt-2 max-w-xl text-sm text-[#6B7280]">{tr("Tell people who you are. These details show up on your public shop profile at")}{" "}
          {shopUrl ? (
            <a
              href={shopUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="shop-public-url"
              className="font-semibold text-[#0F1115] underline-offset-2 transition-colors hover:text-[#FF5757] hover:underline"
            >
              {shopUrl}
            </a>
          ) : (
            <span className="font-semibold text-[#0F1115]">{tr("your shop URL")}</span>
          )}
          .
        </p>
      </header>

      {/* Profile image */}
      <section className="mt-10">
        <h2 className="text-base font-semibold text-[#0F1115]">{tr("Shop profile image")}</h2>
        <p className="text-xs text-[#6B7280]">{tr("Square image · min ")}{LOGO_MIN.width}×{LOGO_MIN.height}{tr(" · JPEG, PNG, WebP · up to 5 MB")}</p>
        <div className="mt-3">
          <LogoBlock
            url={form.profile_image}
            onEdit={openLogoCropper}
            onRemove={() => resetField("profile_image")}
          />
        </div>
      </section>

      {/* Banner */}
      <section className="mt-10">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-base font-semibold text-[#0F1115]">{tr("Banner image")}</h2>
            <p className="text-xs text-[#6B7280]">{tr("Recommended ")}{BANNER_MIN.width}×{BANNER_MIN.height}{tr(" or larger · JPEG, PNG, WebP · up to 5 MB")}</p>
          </div>
        </div>
        <div className="mt-3">
          <BannerBlock
            url={form.cover_image}
            onEdit={openBannerCropper}
            onRemove={() => resetField("cover_image")}
          />
        </div>
      </section>

      {/* Basics */}
      <section className="mt-12 space-y-6">
        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="shop-tagline" className="text-sm font-semibold text-[#0F1115]">{tr("Your tagline")}</label>
            <CharCounter
              value={form.tagline}
              max={TAGLINE_MAX}
              testid="tagline-counter"
            />
          </div>
          <p className="mt-1 text-xs text-[#6B7280]">{tr("A short one-liner shown under your shop name (50 chars max).")}</p>
          <input
            id="shop-tagline"
            type="text"
            data-testid="shop-tagline-input"
            value={form.tagline}
            maxLength={TAGLINE_MAX}
            onChange={(e) =>
              setForm((f) => ({ ...f, tagline: e.target.value }))
            }
            placeholder={tr("Like a friend is showing you around.")}
            aria-invalid={!!fieldErrors.tagline}
            className={cn(
              "humrahe-input mt-2",
              fieldErrors.tagline && "border-[#9B0000] focus:border-[#9B0000]",
            )}
          />
          {fieldErrors.tagline && (
            <p
              data-testid="shop-tagline-error"
              className="mt-1 flex items-center gap-1.5 text-xs font-medium text-[#9B0000]"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              {fieldErrors.tagline}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="shop-bio" className="text-sm font-semibold text-[#0F1115]">{tr("Description")}</label>
            <CharCounter
              value={form.bio}
              max={DESCRIPTION_MAX}
              testid="bio-counter"
            />
          </div>
          <p className="mt-1 text-xs text-[#6B7280]">{tr("Introduce your business in a couple of sentences (250 chars max).")}</p>
          <textarea
            id="shop-bio"
            data-testid="shop-bio-input"
            rows={4}
            value={form.bio}
            maxLength={DESCRIPTION_MAX}
            onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
            placeholder={tr("We curate tiny-group walking tours led by locals in Rome, Florence and Naples…")}
            aria-invalid={!!fieldErrors.bio}
            className={cn(
              "humrahe-input mt-2 resize-none py-3",
              fieldErrors.bio && "border-[#9B0000] focus:border-[#9B0000]",
            )}
          />
          {fieldErrors.bio && (
            <p
              data-testid="shop-bio-error"
              className="mt-1 flex items-center gap-1.5 text-xs font-medium text-[#9B0000]"
            >
              <AlertCircle className="h-3.5 w-3.5" />
              {fieldErrors.bio}
            </p>
          )}
        </div>
      </section>

      {/* Sticky save bar */}
      <div
        data-testid="save-bar"
        className={cn(
          "fixed bottom-0 start-0 end-0 z-30 border-t border-[#EFE4E4] bg-white/95 backdrop-blur transition-transform",
          isDirty ? "translate-y-0" : "translate-y-full",
        )}
      >
        <div className="mx-auto flex max-w-[960px] items-center justify-between gap-3 px-4 py-3 sm:px-6 md:px-10">
          <p className="hidden text-sm text-[#6B7280] sm:block">{tr("You have unsaved changes.")}</p>
          <div className="ms-auto flex items-center gap-2">
            <button
              type="button"
              onClick={discard}
              disabled={saving}
              data-testid="discard-btn"
              className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115] disabled:opacity-50"
            >{tr("Discard")}</button>
            <button
              type="button"
              onClick={save}
              disabled={!canSave}
              data-testid="save-btn"
              className="humrahe-btn-primary inline-flex h-10 items-center rounded-md px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? tr("Saving…") : tr("Save changes")}
            </button>
          </div>
        </div>
      </div>

      <ShopImageCropper
        open={cropper.open}
        imageSrc={cropper.src}
        aspect={cropper.target === "logo" ? LOGO_ASPECT : BANNER_ASPECT}
        cropShape={cropper.target === "logo" ? "round" : "rect"}
        title={cropper.target === "logo" ? tr("Adjust your logo") : tr("Adjust your banner")}
        preserveResolution
        outputSize={
          cropper.target === "logo"
            ? { width: 800, height: 800 }
            : { width: 1600, height: 500 }
        }
        onClose={() => setCropper({ open: false, target: null, src: null })}
        onApply={handleCropApply}
      />
    </main>
  );
}
